// - LOAN DETAILS
// - purpose: Refinance, Purchase, FHA Streamline, VA IRRRL
// - value / purch price
// - balance
// - cash out
// - state
// - fico
// - lock term: 30 Days,45 Days,60 Days,90 Days
// - occupancy: Primary, Second Home, Investment
// - property type: Single Family, Condo, Townhome, 2-4 Unit, Manufactured
// - escrow waiver: YES/NO
// - subordination: YES/NO

// - veteran: YES/NO
// - veteran funding fee: 0, 2.15, 3.3

// SETTLEMENT CHARGES
// - title fees
// - appraisal est: 660
// - credit fee: 90
// - processing: waived, 495, 800

// LOAN AMOUNT FACTORS
// - balance + cash: balance+cashOut
// - total revenue: balance+cash * selected bps
// - closing costs
// - starting LTV
// - HOI monthly
// - taxes monthly
// - reserve months
// - escrow monthly

// - selected bps
// - flat dollar

// - max ltv (based on loan type)
// - par option (sum of details, costs, comp)

// OPTION
// - loan program: FHA, VA, conventional, NonQM, Jumbo
// - borrower paid: YES/NO default: YES
// - fees in price: YES/NO default: YES
// - term: NEED,30yr Fixed,25yr Fixed,20yr Fixed,15yr Fixed,10yr Fixed,15yr ARM,10yr ARM,7yr ARM,5yr ARM,3yr ARM default: 30yr
// - rate - input
// - rate price - input
// - loan amount: (sum par option, ufmip, va fee, etc)
// - lender fee: (usually 0 - so what's up with that)
// - mortgage insurance
// - ltv
// - rate credit/cost
// - rate cost or credit
// - cash out / needed
// - emc comp
// - term months
// - APOR
// - APR
// - difference
// - max bona fide %
// - max bona fide amount: $
// - bona fide dollar amount: $
// - points lender fees: $
// - title fees
// - mip/funding fee
// - prepaid interest
// - total loan amount
// - amount financed
// - max fees 3%
// - difference
// - qm points fees fail: YES/NO
// - VA qm fail - EPM: YES/NO
// - new loan revenue
// - emc revenue split
// - branch revenue split
// - lo revenue split

import startCase from 'lodash.startcase';

import { getMonthsToCollect } from '@leads/core/data';

const round = (num: number): number => {
  return Math.round((num + Number.EPSILON) * 100) / 100;
};

const PMT = (ir: number, np: number, pv: number, fv: number = 0, type: number = 0): number => {
  if (ir === 0) return -(pv + fv) / np;
  var pvif = Math.pow(1 + ir, np);
  var pmt = (-ir * (pv * pvif + fv)) / (pvif - 1);
  if (type === 1) pmt /= 1 + ir;
  return pmt;
};

const RATE = (nper: number, pmt: number, pv: number, fv: number = 0, type: number = 0, guess: number = 0.1): number => {
  // Sets the limits for possible guesses to any
  // number between 0% and 100%
  var lowLimit = 0;
  var highLimit = 1;

  // Defines a tolerance of up to +/- 0.00005% of pmt, to accept
  // the solution as valid.
  var tolerance = Math.abs(0.00000005 * pmt);

  // Tries at most 40 times to find a solution within the tolerance.
  for (var i = 0; i < 40; i++) {
    // Resets the balance to the original pv.
    var balance = pv;

    // Calculates the balance at the end of the loan, based
    // on loan conditions.
    for (var j = 0; j < nper; j++) {
      if (type == 0) {
        // Interests applied before payment
        balance = balance * (1 + guess) + pmt;
      } else {
        // Payments applied before insterests
        balance = (balance + pmt) * (1 + guess);
      }
    }

    // Returns the guess if balance is within tolerance.  If not, adjusts
    // the limits and starts with a new guess.
    if (Math.abs(balance + fv) < tolerance) {
      return guess;
    } else if (balance + fv > 0) {
      // Sets a new highLimit knowing that
      // the current guess was too big.
      highLimit = guess;
    } else {
      // Sets a new lowLimit knowing that
      // the current guess was too small.
      lowLimit = guess;
    }

    // Calculates the new guess.
    guess = (highLimit + lowLimit) / 2;
  }

  // Returns null if no acceptable result was found after 40 tries.
  return 0;
};

const advancePmt = (
  monthlyRate: number,
  monthlyPayments: number,
  presentValue: number,
  residualValue: number,
  advancedPayments: number
): number => {
  var t1 = 1 + monthlyRate;
  var t2 = Math.pow(t1, monthlyPayments);
  var t3 = Math.pow(t1, monthlyPayments - advancedPayments);
  return (presentValue - residualValue / t2) / ((1 - 1 / t3) / monthlyRate + advancedPayments);
};

interface Quoteable {}

export interface QuoteInput {
  bfn: string;
  bln: string;
  state: string;
  fico: number;
  balance: number;
  value: number;
  zip: number;
  street: string;
  rateSheet: any[];
  fhaLimit: number;
  currentType: string;
  titleFees: number;
  hoiMonthly: number;
  taxesMonthly: number;
  loEmail: string;
  loName: string;
  loPhone: string;
  loId: string;
  selectedBPS: number;
  isIrrrl?: boolean;
  isFHAStreamline?: boolean;
  cashOutInput?: number;
  loanTypeInput?: 'fha' | 'conventional' | 'nonqm' | 'va';
  purpose?: 'Purchase' | 'Refinance' | 'FHA Streamline' | 'VA IRRRL';
  occupancy?: 'Primary' | 'Second Home' | 'Investment';
  structure?: 'Single Family' | 'Condo' | 'Townhome' | '2-4 Unit' | 'Manufactured';
  pmiAmount?: number;
  manualQuote?: boolean;
  includeFundingFee?: boolean;
  // Optional override for the max LTV cap, expressed as a percentage
  // with up to 3 decimal places (e.g. 74.245 -> 0.74245 internally).
  // Pass -1 (or omit) to fall back to the program/purpose default in
  // QuoteUtils.programs.
  maxLTVInput?: number;
}

export interface QuoteOption {
  pmi: number;
  mpr: number;
  ltv: number;
  monthlyPI: number;
  firstLoanAmount: number;
  aprLoanAmount: number;
  amortizationYears: string;
  amortizationLength: number;
  loanAmount: number;
  vaJumboAdjust: number;
  vaLtvAdjust: number;
  vaTotalAdjust: number;
  cashOutAmount: number;
  mipFundingFee: number;
  pointsOrCredit: number;
  totalLoanAmount: number;
  totalSettlement: number;
  rateCreditCostB32: number;
  rate: number;
  price: number;
  label: string;
  apr: number;
  escrowMonthly: number;
  escrowAccount: number;
  program: 'Conv' | Quote['loanProgram'];
  term: string;
}

export interface QuoteData {
  canQuote: boolean;
  appraisalEstimate: number;
  creditFee: number;
  processing: 0 | 495 | 895;
  amortization: 'Fixed' | 'ARM';
  amortizationLength: 360 | 300 | 240 | 180 | 120 | 84 | 60 | 36;
  amortizationTerm: string | 'ThirtyYear' | 'TwentyFiveYear' | 'TwentyYear' | 'FifteenYear';
  loanProgram: 'fha' | 'conventional' | 'nonqm' | 'va';
  purpose: 'Purchase' | 'Refinance' | 'FHA Streamline' | 'VA IRRRL';
  occupancy: 'Primary' | 'Second Home' | 'Investment';
  structure: 'Single Family' | 'Condo' | 'Townhome' | '2-4 Unit' | 'Manufactured';
  baseLoanAmount: number;
  grossAdjust: number;
  titleFees: number;
  hoiMonthly: number;
  taxesMonthly: number;
  pmiAmount: number;
  reserveMonthsTaxes: number;
  reserveMonthsInsurance: number;
  escrowAccountTaxes: number;
  escrowAccountInsurance: number;
  escrowMonthly: number;
  escrowAccount: number;
  cashOut: number;
  maxLTV: number;
  selectedBPS: number;
  balanceAndCash: number;
  totalRevenue: number;
  totalClosingCost: number;
  totalClosingCostLoan: number;
  flatDollar: number;
  parOption: number;
  bfn: string;
  bln: string;
  state: string;
  fico: number;
  balance: number;
  value: number;
  street: string;
  zip: number;
  rateSheet: any;
  fhaLimit: number;
  currentType: string;
  loEmail: string;
  loName: string;
  loPhone: string;
  loId: string;
  isIrrrl: boolean;
  isFHAStreamline: boolean;
  manualQuote: boolean;
  includeFundingFee: boolean;
  localLTV: number;
  bulletNote: string[];
  options: QuoteOption[];
}

export namespace QuoteUtils {
  export const programs: any = {
    fha: {
      purchase: 0.96,
      refinanceCO: 0.8,
      refinanceRT: 0.96,
      fhaStreamline: 0.96,
    },
    conventional: {
      purchase: 0.96,
      refinanceCO: 0.8,
      refinanceRT: 0.96,
    },
    va: {
      purchase: 0.96,
      refinanceCO: 1,
      refinanceRT: 1,
      vaIRRRL: 1.0,
    },
  };

  export const armLookup = {
    36: '3yr',
    60: '5yr',
    84: '7yr',
    120: '10yr',
  };

  export const amortizationLookup = {
    ThirtyYear: 360,
    TwentyFiveYear: 300,
    TwentyYear: 240,
    FifteenYear: 180,
  };

  export function ltv(loan: number, value: number): number {
    return Number((Math.ceil(loan) / Math.ceil(value)).toFixed(2));
  }

  export function not_used_maxLtv(
    program: 'fha' | 'conventional' | 'va',
    purpose: 'purchase' | 'refinance' | 'fhaStreamline' | 'vaIRRRL',
    cashOut: boolean
  ): number {
    const lookup: string =
      cashOut && purpose === 'refinance'
        ? `refinanceCO`
        : !cashOut && purpose === 'refinance'
          ? 'refinanceRT'
          : purpose;
    return programs[program][lookup];
  }
}

export class Quote implements Quoteable {
  public canQuote: boolean = true;

  appraisalEstimate: number = 690;
  creditFee: number = 123;
  processing: 0 | 495 | 895 = 895;
  amortization: 'Fixed' | 'ARM' = 'Fixed';
  amortizationLength: 360 | 300 | 240 | 180 | 120 | 84 | 60 | 36 = 360;
  amortizationTerm: string | 'ThirtyYear' | 'TwentyFiveYear' | 'TwentyYear' | 'FifteenYear' = 'ThirtyYear';
  loanProgram: 'fha' | 'conventional' | 'nonqm' | 'va' = 'fha';
  purpose: 'Purchase' | 'Refinance' | 'FHA Streamline' | 'VA IRRRL' = 'Refinance';
  occupancy: 'Primary' | 'Second Home' | 'Investment' = 'Primary';
  structure: 'Single Family' | 'Condo' | 'Townhome' | '2-4 Unit' | 'Manufactured' = 'Single Family';
  baseLoanAmount: number = 0;
  grossAdjust: number = 1500;

  titleFees: number = 1242.25;
  hoiMonthly: number = 187;
  taxesMonthly: number = 596.85;
  pmiAmount: number = -1;

  reserveMonthsTaxes: number = 2;
  reserveMonthsInsurance: number = 2;
  escrowAccountTaxes: number = 0;
  escrowAccountInsurance: number = 0;
  escrowMonthly: number = 0;
  escrowAccount: number = 0;

  cashOut: number;
  maxLTV: number;
  selectedBPS: number = 225;
  balanceAndCash: number;
  totalRevenue: number;
  totalClosingCost: number;
  totalClosingCostLoan: number;
  flatDollar: number;
  parOption: number;

  bfn: string;
  bln: string;
  state: string;
  fico: number;
  balance: number;
  value: number = 0;
  street: string;
  zip: number;
  rateSheet: any;
  fhaLimit: number;
  currentType: string;

  loEmail: string;
  loName: string;
  loPhone: string;
  loId: string;
  isIrrrl: boolean = false;
  isFHAStreamline: boolean = false;

  manualQuote: boolean = false;
  includeFundingFee: boolean = false;

  localLTV = QuoteUtils.ltv(this.baseLoanAmount, this.value);

  bulletNote: string[] = [];

  constructor({
    bfn,
    bln,
    state,
    fico,
    balance,
    value,
    zip,
    street,
    rateSheet,
    fhaLimit,
    currentType,
    titleFees,
    hoiMonthly,
    taxesMonthly,
    loEmail,
    loName,
    loPhone,
    loId,
    isIrrrl,
    isFHAStreamline,
    selectedBPS,
    cashOutInput,
    loanTypeInput,
    purpose,
    occupancy,
    structure,
    pmiAmount,
    manualQuote,
    includeFundingFee,
    maxLTVInput,
  }: QuoteInput) {
    this.loId = loId;
    this.loEmail = loEmail;
    this.loName = loName;
    this.loPhone = loPhone;
    this.bfn = startCase(bfn.toLowerCase());
    this.bln = startCase(bln.toLowerCase());
    this.state = state;
    this.fico = fico;
    this.balance = balance;
    this.value = value;
    this.street = street;
    this.zip = zip;
    this.fhaLimit = fhaLimit || 850000;
    this.isIrrrl = isIrrrl || false;
    this.isFHAStreamline = isFHAStreamline || false;

    this.titleFees = titleFees;
    this.hoiMonthly = hoiMonthly;
    this.taxesMonthly = taxesMonthly;
    this.pmiAmount = pmiAmount || -1;
    this.manualQuote = manualQuote || false;
    this.includeFundingFee = includeFundingFee || false;

    this.cashOut = !isIrrrl ? (typeof cashOutInput === 'number' && cashOutInput !== -1 ? cashOutInput : 50000) : 0;
    this.currentType = currentType;
    this.loanProgram = loanTypeInput
      ? (loanTypeInput as 'fha' | 'conventional' | 'nonqm' | 'va')
      : currentType === 'VA'
        ? 'va'
        : 'fha';

    this.purpose = purpose ? purpose : isIrrrl ? 'VA IRRRL' : isFHAStreamline ? 'FHA Streamline' : 'Refinance';
    if (occupancy) this.occupancy = occupancy;
    if (structure) this.structure = structure;

    this.appraisalEstimate = isIrrrl || isFHAStreamline ? 0 : 690;
    this.processing = isIrrrl || isFHAStreamline ? 495 : 895;

    this.maxLTV = QuoteUtils.programs[this.loanProgram][this.cashOut === 0 ? 'refinanceRT' : 'refinanceCO'];
    // Manual override: callers can pin maxLTV by passing a percentage
    // (e.g. 74.245). -1 / omitted means "use the program default above".
    if (typeof maxLTVInput === 'number' && maxLTVInput !== -1) {
      this.maxLTV = maxLTVInput / 100;
    }
    // this.selectedBPS = currentType === 'VA' ? 0 : 225;
    this.selectedBPS = selectedBPS;
    this.balanceAndCash = Number(this.balance + this.cashOut);

    if (this.loanProgram === 'va') {
      if (this.includeFundingFee) {
        const feePct = this.isIrrrl ? 0.5 : 3.3;
        this.bulletNote.push(`This quote includes the VA Funding Fee of ${feePct}%`);
      } else {
        this.bulletNote.push(`This quote does not include the VA Funding Fee`);
      }
    }

    if (isIrrrl || isFHAStreamline) {
      this.reserveMonthsInsurance = 4;
      this.reserveMonthsTaxes = getMonthsToCollect(state);
      this.bulletNote.push(
        `Escrow estimate includes ${this.reserveMonthsTaxes} mos. property taxes, ${this.reserveMonthsInsurance} mos. of home owner's insurance`
      );
    }
    this.escrowMonthly = round(this.hoiMonthly + this.taxesMonthly);
    this.escrowAccountTaxes = round(this.taxesMonthly * this.reserveMonthsTaxes);
    this.escrowAccountInsurance = round(this.hoiMonthly * this.reserveMonthsInsurance);
    this.escrowAccount = this.escrowAccountTaxes + this.escrowAccountInsurance;

    this.totalClosingCost = round(
      Number(
        this.titleFees +
          this.appraisalEstimate +
          this.creditFee +
          this.processing +
          this.escrowAccount -
          this.appraisalEstimate
      )
    );
    this.totalClosingCostLoan = Number(this.titleFees + this.appraisalEstimate + this.creditFee + this.processing);

    this.totalRevenue = round(Number(this.balanceAndCash * (this.selectedBPS * 0.0001)));
    this.baseLoanAmount = round(Number(this.balanceAndCash + this.totalRevenue + this.totalClosingCost));
    this.parOption = Math.min(this.baseLoanAmount, this.value * this.maxLTV);

    this.flatDollar = round(Number(this.baseLoanAmount * (this.selectedBPS * 0.0001)));

    const scenarioBlocks = [500, 580, 620, 640, 660, 680];
    let selectedScenario = scenarioBlocks[0];
    for (let k of scenarioBlocks) {
      if (this.fico >= k) selectedScenario = k;
    }

    this.rateSheet = rateSheet
      ? {
          ...(rateSheet.find(r => r.scenario === `${this.loanProgram}${isIrrrl ? 'Irrrl' : ''}${selectedScenario}`) || {
            rates: [],
          }),
        }
      : { rates: [] };

    this.localLTV = QuoteUtils.ltv(this.baseLoanAmount, this.value);
    this.canQuote = (this.loanProgram === 'fha' && this.baseLoanAmount + this.grossAdjust <= fhaLimit) || value > 0;
  }

  private buildOptions(): QuoteOption[] {
    const rates = Array.isArray(this.rateSheet?.rates) ? this.rateSheet.rates : [];

    // Per VA Circular 26-19-5, when the funding fee is financed, the
    // base loan + financed fee cannot exceed the property's reasonable
    // value. Tighten the effective base-loan LTV cap so that
    // loanAmount * (1 + feeRate) <= value * maxLTV.
    const vaFundingFeeRate =
      this.loanProgram === 'va' && this.includeFundingFee ? (this.isIrrrl ? 0.005 : 0.033) : 0;
    const effectiveMaxLTV = vaFundingFeeRate > 0 ? this.maxLTV / (1 + vaFundingFeeRate) : this.maxLTV;
    const loanAmountCap = this.value * effectiveMaxLTV;

    return rates.map((rate: any, i: number) => {
      let loanAmount = round(
        Math.min(
          this.baseLoanAmount - ((rate.price - 100) * this.baseLoanAmount) / 100 + this.grossAdjust,
          loanAmountCap
        )
      );
      if (this.purpose === 'Purchase') {
        loanAmount = this.balance;
      }

      const firstLoanAmount = loanAmount;

      if (!this.canQuote || (this.loanProgram === 'fha' && loanAmount > this.fhaLimit)) {
        this.canQuote = false;
      }

      const ltv = round((loanAmount / this.value) * 100);
      let vaJumboAdjust = 0;
      let vaLtvAdjust = 0;
      let vaTotalAdjust = 0;

      // double check the loan amount
      if (this.loanProgram === 'va' && !this.isIrrrl && !this.manualQuote) {
        vaJumboAdjust = loanAmount > 806500 ? -0.05 : 0;
        vaLtvAdjust = ltv > 90 ? -1.6 : 0;
        vaTotalAdjust = round(vaJumboAdjust + vaLtvAdjust);
        loanAmount = round(
          Math.min(
            this.baseLoanAmount - ((rate.price - 100 + vaTotalAdjust) * this.baseLoanAmount) / 100 + this.grossAdjust,
            loanAmountCap
          )
        );
      }

      let rateCreditCostB32 = round(((rate.price - 100 + vaTotalAdjust) * loanAmount) / 100);
      let pointsOrCredit: number = round(Number(this.flatDollar - rateCreditCostB32));
      let totalSettlement: number = round(this.totalClosingCostLoan + pointsOrCredit + this.escrowAccount);

      let cashOutAmount: number = round(loanAmount - this.balance - totalSettlement);
      if (cashOutAmount < -500) {
        this.canQuote = false;
      }

      if (this.purpose === 'Purchase') {
        cashOutAmount = this.value - this.balance + totalSettlement;
      }

      if (
        (this.loanProgram === 'va' && this.isIrrrl) ||
        (this.loanProgram === 'fha' && this.isFHAStreamline && cashOutAmount > 500)
      ) {
        let overage = cashOutAmount - 500;
        cashOutAmount = 500;
        loanAmount = loanAmount - overage;

        rateCreditCostB32 = round(((rate.price - 100 + vaTotalAdjust) * loanAmount) / 100);
        pointsOrCredit = round(Number(this.flatDollar - rateCreditCostB32));
        totalSettlement = round(this.totalClosingCostLoan + pointsOrCredit + this.escrowAccount);
      }

      const mipFundingFee: number =
        this.loanProgram === 'fha'
          ? round(loanAmount * 0.0175)
          : this.loanProgram === 'va' && this.includeFundingFee
            ? round(loanAmount * vaFundingFeeRate)
            : 0;
      const totalLoanAmount: number = round(loanAmount + mipFundingFee);

      const pmi =
        this.pmiAmount !== -1
          ? this.pmiAmount
          : this.loanProgram === 'fha'
            ? round((loanAmount * 0.005) / 12)
            : this.loanProgram === 'conventional' && ltv > 80
              ? round((loanAmount * 0.006) / 12)
              : 0;

      const amortizationLength = this.rateSheet.amortizationTerm
        ? QuoteUtils.amortizationLookup[this.rateSheet.amortizationTerm as keyof typeof QuoteUtils.amortizationLookup]
        : 360;
      const amortizationYears = `${amortizationLength / 12}`;
      const monthlyPI = round(PMT(rate.rate / 100 / 12, amortizationLength, -totalLoanAmount));

      const aprLoanAmount = totalLoanAmount - pointsOrCredit - 450 - pmi * 110;
      const mpr = RATE(amortizationLength, -monthlyPI, aprLoanAmount);

      // Reported LTV reflects what's actually being secured against the
      // property — when the VA funding fee is financed it rides on top
      // of the base loan and is part of the recorded loan amount, so
      // expose totalLoanAmount-based LTV here. Internal `ltv` (used for
      // VA pricing tiers + conventional PMI threshold) keeps base-loan
      // semantics unchanged.
      const reportedLtv =
        this.loanProgram === 'va' && this.includeFundingFee
          ? round((totalLoanAmount / this.value) * 100)
          : ltv;

      return {
        pmi,
        mpr,
        ltv: reportedLtv,
        monthlyPI,
        firstLoanAmount,
        aprLoanAmount,
        amortizationYears,
        amortizationLength,
        loanAmount,
        vaJumboAdjust,
        vaLtvAdjust,
        vaTotalAdjust,
        cashOutAmount,
        mipFundingFee,
        pointsOrCredit,
        totalLoanAmount,
        totalSettlement,
        rateCreditCostB32,
        rate: rate.rate,
        price: rate.price,
        label: `Option ${i + 1}`,
        apr: round(mpr * 12 * 100),
        escrowMonthly: this.escrowMonthly,
        escrowAccount: this.escrowAccount,
        program: this.loanProgram === 'conventional' ? 'Conv' : this.loanProgram,
        term:
          this.rateSheet.amortizationType === 'Fixed'
            ? `${amortizationYears} Year Fixed`
            : `${QuoteUtils.armLookup[this.rateSheet.armFixedTerm as keyof typeof QuoteUtils.armLookup]} ARM`,
      };
    });
  }

  public compute(): QuoteData {
    const options = this.buildOptions();
    return {
      ...this,
      options, //: options.slice(0, 2),
    };
  }
}
